import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper, CalendarDays, Globe2 } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { StatusBadge } from "@/components/StatusBadge";
import { BeltStrip, EnsoRing } from "@/components/karate";

function formatDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

function SectionHeading({ eyebrow, title, to, linkLabel }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        <p className="mb-1 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-ukf-red">{eyebrow}</p>
        <h2 className="pt-4 font-serif text-3xl font-bold tracking-tight text-ukf-ink sm:text-4xl">{title}</h2>
      </div>
      {to && (
        <Link to={to} data-testid={`home-viewall-${eyebrow.toLowerCase()}`} className="group hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-ukf-red sm:flex">
          {linkLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [feds, setFeds] = useState([]);

  useEffect(() => {
    api.get("/public/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/public/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/public/federations").then((r) => setFeds(r.data)).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => {
    try { return new Date(e.date) >= new Date(new Date().toDateString()); } catch { return true; }
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-ukf-obsidian text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:34px_34px] opacity-40" />
        <div className="pointer-events-none absolute inset-0 kagome-dark opacity-70" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 22%, rgba(139,24,27,0.30) 0%, transparent 62%)" }} />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ukf-gold/50 to-transparent" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
          <span className="select-none whitespace-nowrap font-serif leading-none text-white/[0.04] text-7xl sm:text-9xl">空手道連盟連合</span>
        </div>
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-4 py-20 text-center sm:px-6 md:py-28 lg:px-8">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="relative">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[230px] w-[230px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ukf-red shadow-[0_0_80px_25px_rgba(139,24,27,0.45)] sm:h-[280px] sm:w-[280px]" />
            <EnsoRing className="pointer-events-none absolute left-1/2 top-1/2 h-[250px] w-[250px] -translate-x-1/2 -translate-y-1/2 opacity-50 sm:h-[300px] sm:w-[300px]" stroke="#E5C158" strokeWidth={1.5} />
            <div className="relative inline-flex items-center justify-center rounded-full border-2 border-ukf-gold/70 bg-white p-5 shadow-[0_10px_40px_rgba(0,0,0,0.45)] sm:p-6">
              <img src="/branding/ukf-emblem.png" alt="UKF Official Emblem" className="h-20 w-auto object-contain sm:h-28" />
            </div>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }}
            className="mt-24 rounded-full border border-ukf-gold/40 bg-white/5 px-4 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-ukf-gold sm:mt-28">
            Official Website
          </motion.span>
          <motion.h1 data-testid="hero-title" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 font-serif text-3xl font-bold uppercase tracking-wide text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)] sm:mt-16 sm:text-4xl lg:text-5xl">
            <span className="block leading-none">Union of Karate</span>
            <span className="mt-6 block leading-none sm:mt-8">Federations</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="ukf-gold-rule mx-auto mt-7" />
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.45 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/federations" data-testid="hero-cta-federations"
              className="group inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-ukf-red px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-ukf-red-dark active:scale-[0.98]">
              Affiliated Federations <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/news" data-testid="hero-cta-news"
              className="inline-flex items-center gap-2 rounded-md border border-ukf-gold/40 bg-slate-900/60 px-6 py-3.5 text-sm font-semibold text-slate-200 backdrop-blur-sm transition-all hover:border-ukf-gold hover:text-white active:scale-[0.98]">
              Latest News
            </Link>
          </motion.div>
        </div>
        <BeltStrip className="absolute inset-x-0 bottom-0 h-1.5" />
      </section>

      {/* News */}
      {news.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8" data-testid="home-news-section">
          <Reveal><SectionHeading eyebrow="News" title="Latest News" to="/news" linkLabel="All news" /></Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {news.slice(0, 3).map((n, i) => (
              <Reveal key={n.id} delay={i * 0.06}>
                <Link to={`/news/${n.id}`} data-testid={`home-news-card-${n.id}`}
                  className="group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-ukf-gold hover:shadow-md">
                  <p className="font-mono text-xs font-semibold uppercase tracking-wider text-ukf-gold-dark">{formatDate(n.date)}</p>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-ukf-ink transition-colors group-hover:text-ukf-red">{n.title}</h3>
                  <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-ukf-red">
                    Read more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Events */}
      {upcoming.length > 0 && (
        <section className="border-y border-slate-200 bg-white" data-testid="home-events-section">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
            <Reveal><SectionHeading eyebrow="Events" title="Upcoming Events" to="/events" linkLabel="All events" /></Reveal>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {upcoming.slice(0, 4).map((e, i) => (
                <Reveal key={e.id} delay={i * 0.06}>
                  <Link to={`/events/${e.id}`} data-testid={`home-event-card-${e.id}`}
                    className="group flex gap-5 rounded-lg border border-slate-200 bg-ukf-canvas p-5 transition-all hover:border-ukf-gold hover:shadow-md">
                    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg bg-ukf-red text-white">
                      <span className="font-serif text-2xl font-bold leading-none">{new Date(e.date).getDate()}</span>
                      <span className="text-[10px] font-semibold uppercase">{new Date(e.date).toLocaleDateString("en-GB", { month: "short" })}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-serif text-lg font-semibold text-ukf-ink group-hover:text-ukf-red">{e.name}</h3>
                      {e.location && <p className="mt-1 text-sm text-slate-500">{e.location}</p>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Federations */}
      {feds.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8" data-testid="home-federations-section">
          <Reveal><SectionHeading eyebrow="Members" title="Affiliated Federations" to="/federations" linkLabel="View directory" /></Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {feds.slice(0, 6).map((f, i) => (
              <Reveal key={f.id} delay={i * 0.05}>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-5">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-ukf-ink">{f.name}</h3>
                    <p className="text-sm text-slate-500">{f.country}</p>
                  </div>
                  <StatusBadge status={f.status} />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Fallback when totally empty */}
      {news.length === 0 && upcoming.length === 0 && feds.length === 0 && (
        <section className="relative mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
          <div className="relative mx-auto mb-8 flex h-32 w-32 items-center justify-center">
            <EnsoRing className="absolute inset-0 h-full w-full opacity-40" stroke="#C9A24B" strokeWidth={3} />
            <span className="font-serif text-2xl tracking-tight text-ukf-red">空手道</span>
          </div>
          <h2 className="font-serif text-3xl font-semibold text-slate-800">Official content coming soon</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-500">
            News, events and affiliated federations will appear here as they are published.
          </p>
        </section>
      )}
    </div>
  );
}
